import { ResolvedComponentsConfiguration } from "@zoovu/runner-browser-api";
import chroma from "chroma-js";
import { AnswerAnimationStyleClass } from "../abstract/animation";
import mediaQuery from "../abstract/media-query";
import globals from "../partials/global-variables";
import infoTextTrigger from "../partials/info-text-trigger";
import { choiceAnswerTransitions } from "../partials/transition";
import stylesConfig from "../styles.config";

const infoTextBackground = globals.colors.transparent;
const answerTextColor = `${chroma(globals.colors.contrastDark).alpha(0.9)}`;
const answerContentBackground = globals.colors.transparent;
const answerContentSelectedBackground = globals.colors.transparent;
const infoTextTriggerFontColor = `${chroma(globals.colors.contrastDark).alpha(0.6)}`;
const boxShadowColorMoveUp = stylesConfig.colors.primary.spectrum400;

const answerTextFontSize = stylesConfig.typography.fontSize(1.125);
const answerInfoTextFontSize = stylesConfig.typography.fontSize(0.875);
export default {
  container: {
    extend: choiceAnswerTransitions,
    border: "none",
    backgroundColor: answerContentBackground,
    "& label": {
      padding: "10px",
      cursor: "pointer",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      height: "100%",
      [`@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)`]: {
        height: "auto",
      },
      [`@supports (-ms-ime-align:auto)`]: {
        height: "auto",
      },
    },
    "& .image-element": {
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      right: 0,
      backgroundSize: "cover",
      backgroundPosition: "center",
      borderRadius: "50%",
    },
    "& .answer-text": {
      display: "flex",
      justifyContent: "center",
      textAlign: "center",
      padding: "15px 10px 15px",
      width: "100%",
      wordBreak: "break-word",
      color: answerTextColor,
      fontSize: answerTextFontSize,
      fontWeight: 500,
      backgroundColor: globals.colors.gray,
      borderColor: globals.colors.gray,
      position: "relative",
      borderRadius: "25px",
      marginTop: "10px",
      marginBottom: "10px",
      alignItems: "center",
      lineHeight: "1em",
      [`@media (${globals.breakpoints.$xs})`]: {
        fontSize: stylesConfig.typography.fontSize(0.875),
      },
    },
    "& .answer-info-text": {
      position: "relative",
      width: "100%",
      height: "100%",
      padding: "10px",
      background: infoTextBackground,
      lineHeight: "1.2em",
      letterSpacing: ".25px",
      fontSize: answerInfoTextFontSize,
      fontWeight: 400,
      color: infoTextTriggerFontColor,
      wordBreak: "break-word",
      textAlign: "center",
    },
    "& .answer-content": {
      display: "flex",
      flex: "2 1 auto",
      alignItems: "stretch",
      justifyContent: "flex-start",
      flexWrap: "wrap",
      [`@media (${globals.breakpoints.$xs})`]: {
        width: "100%",
      },
    },
    "&.is-selected": {
      border: "none",
      "& .answer-content": {
        background: answerContentSelectedBackground,
        "& .answer-text": {
          background: stylesConfig.colors.secondaryAccentColorVariants.dark,
          color: stylesConfig.colors.secondaryFontColor,
        },
      },
      "& .answer-icon-wrapper": {
        "& .answer-icon": {
          backgroundColor: stylesConfig.colors.primary.spectrum400,
        },
      },
    },
    "& .answer-content input": {
      display: "none",
    },
    ...infoTextTrigger,
    "&.info-test-shown .answer-info-text-trigger": {
      background: (configuration: ResolvedComponentsConfiguration): string =>
        `${globals.customerSprite(configuration)} no-repeat -24px -147px`,
    },
    "& .answer-icon-wrapper": {
      width: "100%",
      position: "absolute",
      left: 0,
      bottom: "18px",
      "& .answer-icon": {
        width: "35px",
        height: "35px",
        margin: "0 auto",
        backgroundColor: stylesConfig.colors.secondary.spectrum200,
        borderRadius: "50%",
        position: "relative",
        [`@media (${globals.breakpoints.$xs})`]: {
          width: "20px",
          height: "20px",
        },
        "&:after": {
          content: "' '",
          display: "block",
          width: "7px",
          height: "7px",
          backgroundColor: globals.colors.white,
          position: "absolute",
          top: "calc(50% - 3.5px)",
          left: "calc(50% - 3.5px)",
          borderRadius: "50%",
          [`@media (${globals.breakpoints.$xs})`]: {
            width: "4px",
            height: "4px",
            top: "calc(50% - 2px)",
            left: "calc(50% - 2px)",
          },
        },
      },
    },
    "&.is-disabled": {
      "& .answer-image": {
        opacity: 0.4,
        cursor: "default",
      },
      "& .answer-content": {
        opacity: 0.4,
        cursor: "default",
      },
      "& label": {
        cursor: "default",
      },
    },
    [`&.${AnswerAnimationStyleClass.ZOOM_IN}`]: {
      "&.answer .answer-image": {
        transition: "transform 0.3s",
        marginBottom: "7px",
        marginTop: "7px",
      },
      "& .answer-text": {
        transitionProperty: "background-color, color",
        transitionDuration: "0.3s",
      },
      [mediaQuery.hover]: {
        "&:hover": {
          "& .answer-image": {
            transform: "scale(1.1)",
          },
          "&:not(.is-selected) .answer-text": {
            background: globals.colors.darkGray,
          },
          "&.is-selected:not(.has-image) .answer-text": {
            background: stylesConfig.colors.secondary.spectrum700,
          },
        },
      },
      "&.is-selected": {
        "& .answer-content .answer-text": {
          background: stylesConfig.colors.secondaryAccentColorVariants.dark,
          color: stylesConfig.colors.secondaryFontColor,
        },
      },
    },
    [`&.${AnswerAnimationStyleClass.MOVE_UP}`]: {
      "&.answer": {
        transitionProperty: "margin-top",
        transitionDuration: "0.3s",
        "& .answer-text": {
          transitionProperty: "background-color, color",
          transitionDuration: "0.3s",
        },
        "& .answer-image": {
          transform: "scale(0.94)",
          marginBottom: "7px",
          marginTop: "10px",
          "&::before": {
            position: "absolute",
            content: "''",
            borderRadius: "50%",
            height: "100%",
            width: "100%",
            top: "50%",
            left: "50%",
            padding: "3px",
            boxShadow: (configuration: ResolvedComponentsConfiguration): string =>
              `0 0 0 4px ${boxShadowColorMoveUp(configuration)}`,
            transitionProperty: "transform, opacity",
            transitionDuration: "0.3s",
            transform: "translate(-50%, -50%) scale(0.8)",
            opacity: "0",
          },
        },
      },
      [mediaQuery.hover]: {
        "&:hover": {
          "&.answer": {
            marginTop: "-10px",
            "&.is-selected": {
              "& .answer-image::before": {
                transform: "translate(-50%, -50%) scale(1.05)",
                opacity: "1",
              },
              "& .answer-text": {
                background: stylesConfig.colors.secondaryAccentColorVariants.dark,
                color: stylesConfig.colors.secondaryFontColor,
              },
            },
          },
        },
      },
      "&.answer.is-selected": {
        "& .answer-content .answer-text": {
          background: stylesConfig.colors.secondaryAccentColorVariants.dark,
          color: stylesConfig.colors.secondaryFontColor,
        },
        "& .answer-image::before": {
          transform: "translate(-50%, -50%) scale(1.05)",
          opacity: "1",
        },
      },
    },
  },
};
